import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Button from '@repo/ui/button'
import Checkbox from '@repo/ui/checkbox'
import { DiplomaIcon, PlusIcon } from '@repo/ui/icons'
import MultiSelectInput from '@repo/ui/multiselect-input'
import ScrollableDiv from '@repo/ui/scrollable-div'
import Typography from '@repo/ui/typography'
import { useFormContext, useWatch } from 'react-hook-form'

import useTranslate from '../../../lib/hooks/use-translate'
import { DocumentStatusEnum } from '../../../lib/types/enums/common/document-status.enum'
import {
  DiplomaEnum,
  diplomaEnumMapping
} from '../../../lib/types/enums/staff-application/diploma.enum'
import {
  EducationLevelEnum,
  educationLevelEnumMapping
} from '../../../lib/types/enums/staff-application/education-level.enum'
import {
  SchoolLevelTaughtEnum,
  schoolLevelTaughtEnumMapping
} from '../../../lib/types/enums/staff-application/school-level-taught.enum'
import { Staff } from '../../../lib/types/models/staff/staff'
import { StaffInput } from '../../../lib/types/models/staff/staff-input'
import { getSelectedEnumOption } from '../../../lib/utils/functions/misc.functions'
import { FormControl, FormField, FormItem } from '../../elements/forms/form'
import IconBox from '../../elements/icon-box'
import InformationToolTip from '../../elements/information-tooltip'
import { ControlledAdressInput } from '../../elements/inputs/address-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import DocumentCard from '../staff/document-card'
import {
  diplomasThatRequireFiles,
  diplomasWithPaySlip,
  isStaffPaySlipRequired
} from '../staff/utils/diplomas-required-documents'

type Props = {
  staff?: Pick<Staff, 'qualifications' | 'position'>
}

const StaffPersonalSpaceProfessionalDetailsForm = ({ staff }: Props) => {
  const { t } = useTranslate()

  const form = useFormContext<StaffInput>()

  // Default to empty array to prevent undefined.map() crash when navigating between pages
  const diplomas = useWatch({ name: 'diplomas', control: form.control }) ?? []

  return (
    <IconBox
      title={t('staff.personal.space.professional.details')}
      icon={DiplomaIcon}
      className="flex flex-col gap-3"
      titleColor={tailwindColors.black.DEFAULT}
      titleVariant="h6"
    >
      <Typography variant="h6" color={tailwindColors.black.DEFAULT}></Typography>
      <div className="grid grid-cols-2 gap-3 mobile:grid-cols-1">
        <FormField
          control={form.control}
          name="currentSituation"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput
                  {...field}
                  label={t('staff.personal.space.current.status')}
                  type="text"
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="studiedDomain"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput
                  {...field}
                  label={t('staff.personal.space.field.of.study')}
                  type="text"
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="educationLevel"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  label={t('staff.personal.space.education.level')}
                  options={Object.values(EducationLevelEnum).map((level) => ({
                    label: educationLevelEnumMapping[level],
                    value: level
                  }))}
                  selectedOption={getSelectedEnumOption(field.value, educationLevelEnumMapping)}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="classLevels"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <MultiSelectInput
                  {...field}
                  label={t('staff.personal.space.taught.class.level')}
                  variant="primary"
                  searchable
                  options={Object.values(SchoolLevelTaughtEnum).map((level) => ({
                    label: t(schoolLevelTaughtEnumMapping[level]),
                    value: level
                  }))}
                  selectedOptions={field.value?.map((level) => ({
                    label: t(schoolLevelTaughtEnumMapping[level]),
                    value: level
                  }))}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
        {t('staff.personal.space.establishments')}
      </Typography>
      <div className="grid grid-cols-2 mobile:grid-cols-1 gap-3">
        <FormField
          control={form.control}
          name="establishmentName"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput
                  {...field}
                  label={t('staff.personal.space.establishment.name')}
                  type="text"
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="establishmentAddress"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledAdressInput
                  {...field}
                  label={t('staff.personal.space.establishment.address')}
                  onDetailsChange={(placeDetails) => {
                    form.setValue('establishmentAddress', placeDetails.address, {
                      shouldValidate: true
                    })
                    form.setValue('establishmentPostalCode', placeDetails.postalCode, {
                      shouldValidate: true
                    })
                    form.setValue('establishmentCity', placeDetails.city, {
                      shouldValidate: true
                    })
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="establishmentPostalCode"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput
                  {...field}
                  label={t('staff.personal.space.establishment.postal.code')}
                  type="text"
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="establishmentCity"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput
                  {...field}
                  label={t('staff.personal.space.establishment.city')}
                  type="text"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="flex gap-4 items-center mt-5 mb-3">
        <Typography variant="sub-heading" color={tailwindColors.grey.DEFAULT}>
          {t('staff.personal.space.diplomas')}
        </Typography>
        <InformationToolTip align="start">
          <Typography variant="label-semibold" color={tailwindColors.white.DEFAULT}>
            {t('staff.personal.space.diplomas.info')}
          </Typography>
        </InformationToolTip>
      </div>
      <div className="flex items-center gap-6 flex-wrap" id="diploma-documents">
        {Object.values(DiplomaEnum).map((diplomaEnumValue) =>
          diplomaEnumValue !== DiplomaEnum.OTHER ? (
            <Checkbox
              label={t(diplomaEnumMapping[diplomaEnumValue])}
              key={diplomaEnumValue}
              checked={diplomas?.map((diploma) => diploma.type).includes(diplomaEnumValue)}
              disabled={
                diplomas?.find((diploma) => diploma.type === diplomaEnumValue)?.status ===
                  DocumentStatusEnum.VERIFIED ||
                !!form.formState.defaultValues?.diplomas?.find(
                  (diploma) =>
                    diploma?.type === diplomaEnumValue &&
                    diplomasWithPaySlip.includes(diplomaEnumValue) &&
                    form.formState.defaultValues?.paySlipStatus === DocumentStatusEnum.VERIFIED
                )
              }
              onChange={(checked) => {
                if (checked)
                  form.setValue('diplomas', [
                    ...(form.getValues('diplomas') ?? []),
                    { type: diplomaEnumValue, status: DocumentStatusEnum.PENDING }
                  ])
                else {
                  const remainingDiplomas = diplomas.filter(
                    (diploma) => diploma.type !== diplomaEnumValue
                  )
                  form.setValue('diplomas', remainingDiplomas)
                  // A teacher keeps the pay slip whatever their diplomas
                  if (
                    diplomasWithPaySlip.includes(diplomaEnumValue) &&
                    !isStaffPaySlipRequired({
                      position: staff?.position,
                      qualifications: staff?.qualifications,
                      diplomas: remainingDiplomas,
                      memberOfNationalEducationStatus:
                        form.getValues('application')?.memberOfNationalEducationStatus
                    })
                  ) {
                    form.setValue('paySlipStatus', DocumentStatusEnum.PENDING)
                    form.setValue('paySlip', undefined)
                  }
                }
              }}
            />
          ) : (
            <Button
              type="button"
              key={diplomaEnumValue}
              variant="ghost"
              className="px-4 py-3"
              icon={PlusIcon}
              label={t(diplomaEnumMapping[diplomaEnumValue])}
              onClick={() => {
                form.setValue('diplomas', [
                  ...(form.getValues('diplomas') ?? []),
                  {
                    type: DiplomaEnum.OTHER,
                    status: DocumentStatusEnum.PENDING
                  }
                ])
              }}
            />
          )
        )}
      </div>
      <ScrollableDiv className="flex gap-5 overflow-auto mobile:flex-col flex-row py-5 mobile:overflow-hidden">
        {diplomas.map((diploma, index) => {
          const file = diploma.file
          if (diplomasThatRequireFiles.includes(diploma.type))
            return (
              <FormField
                key={`${diploma.type}-${index}`}
                control={form.control}
                name={`diplomas.${index}.file`}
                render={() => (
                  <DocumentCard
                    id={diploma.type}
                    className="mobile:w-full w-72 flex-shrink-0"
                    disabled={diploma.status === DocumentStatusEnum.VERIFIED}
                    errorMessage={form.formState.errors.diplomas?.[index]?.file?.message}
                    title={t(diplomaEnumMapping[diploma.type])}
                    onFilesChange={(files) => {
                      form.setValue(`diplomas.${index}.file`, files[0], { shouldValidate: true })
                      form.setValue(
                        `diplomas.${index}.status`,
                        files[0] ? DocumentStatusEnum.RECEIVED : DocumentStatusEnum.PENDING
                      )
                    }}
                    files={file ? [file] : []}
                    status={diploma.status}
                    statusPosition="inside"
                    onStatusChange={(status) => {
                      form.setValue(`diplomas.${index}.status`, status)
                    }}
                    canRemove={diploma.type === DiplomaEnum.OTHER}
                    onRemoveCard={() => {
                      if (diploma.type === DiplomaEnum.OTHER) {
                        form.setValue(
                          'diplomas',
                          diplomas.filter((_, i) => i !== index)
                        )
                      }
                    }}
                  >
                    {diploma.type === DiplomaEnum.OTHER ? (
                      <>
                        <FormField
                          control={form.control}
                          name={`diplomas.${index}.diplomaTitle`}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <ControlledInput
                                  {...field}
                                  label={t('staff.personal.space.degree.name')}
                                  type="text"
                                  disabled={diploma.status === DocumentStatusEnum.VERIFIED}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <div className="h-4" />
                      </>
                    ) : null}
                  </DocumentCard>
                )}
              />
            )
        })}
      </ScrollableDiv>
    </IconBox>
  )
}

export default StaffPersonalSpaceProfessionalDetailsForm
